import { ChangeDetectionStrategy, Component, input, computed } from '@angular/core';
import { Badge, BadgeColor } from '../badge/badge';
import { ZagIcon } from '../icon/icon';
import { ZagDateTimePipe } from '../formatting/zag-date-time.pipe';

export interface TimelineItem {
  /** Unique identifier */
  id: string | number;
  /** Main title/description of the event */
  title: string;
  /** Optional subtitle or secondary info */
  subtitle?: string;
  /** Timestamp of the event */
  timestamp: Date | string;
  /** Icon name (Material Symbols) */
  icon?: string;
  /** Color variant for the icon/indicator */
  color?: BadgeColor;
  /** Optional badge text */
  badge?: string;
  /** Optional user/actor name */
  actor?: string;
}

/** Vertical timeline of events with icons, timestamps and descriptions. */
@Component({
  selector: 'zag-timeline',
  imports: [ZagDateTimePipe, Badge, ZagIcon],
  template: `
    <div class="timeline">
      @for (item of items(); track item.id; let last = $last) {
        <div class="timeline__item" [class.timeline__item--last]="last">
          <div class="timeline__indicator">
            <div
              class="timeline__dot"
              [class.timeline__dot--brand]="item.color === 'brand'"
              [class.timeline__dot--success]="item.color === 'success'"
              [class.timeline__dot--warning]="item.color === 'warning'"
              [class.timeline__dot--error]="item.color === 'error'"
              [class.timeline__dot--info]="item.color === 'info'"
              [class.timeline__dot--neutral]="item.color === 'neutral' || !item.color"
            >
              @if (item.icon) {
                <span [zagIcon]="item.icon" class="timeline__icon"></span>
              }
            </div>
            @if (!last) {
              <div
                class="timeline__line"
                [class.timeline__line--brand]="item.color === 'brand'"
                [class.timeline__line--success]="item.color === 'success'"
                [class.timeline__line--warning]="item.color === 'warning'"
                [class.timeline__line--error]="item.color === 'error'"
                [class.timeline__line--info]="item.color === 'info'"
                [class.timeline__line--neutral]="item.color === 'neutral' || !item.color"
              ></div>
            }
          </div>
          <div class="timeline__content">
            <div class="timeline__header">
              <span class="timeline__title">{{ item.title }}</span>
              @if (item.badge) {
                <zag-badge [color]="item.color ?? 'neutral'">{{ item.badge }}</zag-badge>
              }
            </div>
            @if (item.subtitle) {
              <p class="timeline__subtitle">{{ item.subtitle }}</p>
            }
            <div class="timeline__meta">
              @if (item.actor) {
                <span class="timeline__actor">{{ item.actor }}</span>
                <span class="timeline__separator">-</span>
              }
              <time class="timeline__time" [attr.datetime]="item.timestamp">{{ item.timestamp | zagDateTime }}</time>
            </div>
          </div>
        </div>
      } @empty {
        <div class="timeline__empty">
          <ng-content select="[slot=empty]">
            <span>{{ emptyMessage() }}</span>
          </ng-content>
        </div>
      }
    </div>
  `,
  styleUrl: './timeline.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Timeline {
  /** List of timeline items to display */
  readonly items = input<TimelineItem[]>([]);

  /** Message to show when no items */
  readonly emptyMessage = input<string>('No activity to display');

  /** Maximum number of items to show (0 = unlimited) */
  readonly maxItems = input<number>(0);

  /** Computed items with optional limit */
  readonly displayItems = computed(() => {
    const items = this.items();
    const max = this.maxItems();
    return max > 0 ? items.slice(0, max) : items;
  });
}
